import { useState, useCallback } from "react";
import { UploadImageResp } from "@garther/interfaces";

interface UseUploadImageOptions {
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

interface UseUploadImageReturn {
  uploadImage: (
    file: File,
    uploadFn: (file: File) => Promise<UploadImageResp>
  ) => Promise<string | null>;
  isLoading: boolean;
  error: Error | null;
  url: string | null;
  reset: () => void;
}

/**
 * Hook for uploading images
 *
 * @param options - Configuration options
 * @param options.onSuccess - Callback when upload succeeds
 * @param options.onError - Callback when upload fails
 * @returns Object containing upload function, loading state, error, url, and reset function
 *
 * @example
 * ```tsx
 * const { uploadImage, isLoading, error, url } = useUploadImage({
 *   onSuccess: (url) => console.log('Uploaded:', url),
 *   onError: (error) => console.error('Error:', error)
 * });
 *
 * const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0];
 *   if (file) {
 *     await uploadImage(file, publicApiClient.uploadImage);
 *   }
 * };
 * ```
 */
export function useUploadImage(
  options: UseUploadImageOptions = {}
): UseUploadImageReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  const uploadImage = useCallback(
    async (
      file: File,
      uploadFn: (file: File) => Promise<UploadImageResp>
    ): Promise<string | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await uploadFn(file);
        const imageUrl = response.url;
        setUrl(imageUrl);
        options.onSuccess?.(imageUrl);
        return imageUrl;
      } catch (err) {
        console.log(err);
        const error = err instanceof Error ? err : new Error("Upload failed");
        setError(error);
        options.onError?.(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [options]
  );

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setUrl(null);
  }, []);

  return {
    uploadImage,
    isLoading,
    error,
    url,
    reset,
  };
}
